import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SearchInput } from "../../../../components/ui/SearchInput";
import type { CurrentUser } from "../../../../domains/user/hooks/useCurrentUser";
import { saveMainSpecialty } from "../../../../domains/user/saveMainSpecialty";
import { useSpecialtySearch } from "../../../../domains/app/useLabelSearchAssist";
import { getItemTranslation } from "../../../../domains/app/utils/getItemTranslation";
import { useApiQuery } from "../../../../services/api";
import { HIDDEN_SPECIALTY_IDS } from "../../../../tools/utils";
import { displayToast } from "../../../../components/app/AppToast";
import Loader from "../../../../components/Loader";
import {
  SettingsOptionRow,
  SettingsPage,
  SettingsSection,
} from "../components/SettingsPanel";

/**
 * My main specialty (Figma "D3.settings.specialty" and "…specialty.search"):
 * the specialties this profession declares, a search over them, and a check
 * on the one held. A pick writes at once.
 *
 * Same list as the mobile sheet and onboarding: the parent tags minus the
 * hidden ones, scoped to the profession when it declares a list, plus the
 * held one.
 */
export function SpecialtySection({
  currentUser,
}: {
  currentUser: CurrentUser;
}): React.JSX.Element {
  const { t } = useTranslation();
  const specialtiesQuery = useApiQuery("/webapp/public/tags/parents", {
    durable: true,
  });
  // RHF holds the search text (no useState in a business component) and the
  // in-flight flag of a pick.
  const { register, watch, handleSubmit, formState } = useForm<{
    query: string;
  }>({ defaultValues: { query: "" } });
  const query = watch("query");

  const profession = currentUser.professionModel;
  const held = currentUser.mainSpecialtyModel;
  const all = specialtiesQuery.state === "success" ? specialtiesQuery.body : [];
  const scoped = all
    .filter((specialty) => !HIDDEN_SPECIALTY_IDS.has(specialty.id))
    .filter((specialty) =>
      profession === null || profession.medical_specialties.length === 0
        ? true
        : profession.medical_specialties.includes(specialty.id)
    );
  // The held specialty stays listed even when that scoping drops it (a hidden
  // one, or one the profession does not declare): the page shows what the
  // account holds, checked, and the reader picks another one from there.
  const heldRow =
    held === null || scoped.some((specialty) => specialty.id === held.id)
      ? undefined
      : all.find((specialty) => specialty.id === held.id);
  const specialties = heldRow === undefined ? scoped : [...scoped, heldRow];
  const search = useSpecialtySearch({ specialties, query });

  async function pick(id: string): Promise<void> {
    if (held !== null && held.id === id) return;
    const result = await saveMainSpecialty({
      from: held === null ? null : held.id,
      to: id,
    });
    if (result === "failed") {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    displayToast(
      t("Specialty updated. We'll tune your recommendations."),
      "success"
    );
  }

  return (
    <SettingsPage back={null}>
      <SettingsSection
        title={t("My main specialty")}
        description={t("Select your main specialty to personalize your feed.")}
      >
        <SearchInput
          className="mt-2.5"
          placeholder={t("Specify my specialty")}
          {...register("query")}
        />
        {specialtiesQuery.state === "error" ? (
          <p className="text-sm text-ds-slate-700">
            {specialtiesQuery.message}
          </p>
        ) : specialtiesQuery.state === "pending" ? (
          <Loader expanded />
        ) : (
          <div className="flex flex-col gap-0.5 pt-3">
            {search.rows.map((specialty) => (
              <SettingsOptionRow
                key={specialty.id}
                lines={1}
                selected={held !== null && held.id === specialty.id}
                disabled={formState.isSubmitting}
                onSelect={() => {
                  void handleSubmit(() => pick(specialty.id))();
                }}
              >
                <span
                  className={
                    held !== null && held.id === specialty.id
                      ? "text-sm font-semibold text-ds-slate-950"
                      : "text-sm font-medium text-ds-slate-950"
                  }
                >
                  {getItemTranslation(specialty)}
                </span>
              </SettingsOptionRow>
            ))}
          </div>
        )}
      </SettingsSection>
    </SettingsPage>
  );
}
